import React, { Fragment, useEffect, useState } from "react";

import { useFormContext } from "react-hook-form/dist/react-hook-form.ie11";

import { Insurance, Vehicle, VehicleAssignment } from "@hubcapp/types";
import { UpdateMileageModal } from "@hubcapp/components";
import { useAuth, useTealium } from "@hubcapp/context";

import { DrivingDetailsStateProviderProps } from "./types";
import {
  getLatestVehicleMileage,
  getUpdatedVehicleAssignments,
} from "../../helpers";
import DrivingDetails from "./DrivingDetails";
import InsuranceModal from "./components/InsuranceModal";

const StateProvider: React.FC<DrivingDetailsStateProviderProps> = ({
  loading = false,
  certifications = [],
  additionalDrivers = [],
  onMileageUpdate = () => {},
}) => {
  const [
    vehicleAssignmentToUpdate,
    setVehicleAssignmentToUpdate,
  ] = useState<VehicleAssignment | null>();
  const { watch, setValue } = useFormContext();
  const { trackEvent } = useTealium();

  const {
    instructor: { id: instructorId },
  } = useAuth();

  const [updateInsuranceModal, toggleUpdateInsuranceModal] = useState<boolean>(
    false,
  );
  const [vehicleToUpdate, setVehicleToUpdate] = useState<Vehicle | undefined>();

  const vehicleAssignments: VehicleAssignment[] = watch("vehicleAssignments");

  useEffect(() => {
    onMileageUpdate();
  }, [vehicleAssignments]);

  const handleUpdateMileageClick = (assignmentId: string) => {
    if (!vehicleAssignments) return;

    const selectedAssignment = vehicleAssignments.find(
      assignment => assignment.id === assignmentId,
    );

    if (selectedAssignment) {
      setVehicleAssignmentToUpdate(selectedAssignment);
    }

    trackEvent({
      eventCategory: "My Details",
      eventAction: "Button Click",
      eventLabel: "Mileage Update",
    });
  };

  const handleUpdateMileageDiscardClick = () => {
    trackEvent({
      eventCategory: "My Details",
      eventAction: "Button Click",
      eventLabel: "Mileage Discard",
    });
    setVehicleAssignmentToUpdate(null);
  };

  const handleUpdateMileageSaveClick = (updatedMileage: number) => {
    if (!vehicleAssignmentToUpdate) return;

    setValue(
      "vehicleAssignments",
      getUpdatedVehicleAssignments(
        vehicleAssignments,
        vehicleAssignmentToUpdate,
        updatedMileage,
      ),
    );
    setVehicleAssignmentToUpdate(null);

    trackEvent({
      eventCategory: "My Details",
      eventAction: "Button Click",
      eventLabel: "Mileage Save",
    });
  };

  const handleUpdateClick = (id: string) => {
    setVehicleToUpdate(
      vehicleAssignments.find(vehicleAss => vehicleAss.id === id)?.vehicle ||
        undefined,
    );
    toggleUpdateInsuranceModal(true);
  };

  const handleUpdateInsuranceSaveClick = (insurance: Insurance) => {
    toggleUpdateInsuranceModal(false);

    const assignment = vehicleAssignments.find(
      va => va.vehicle.id === vehicleToUpdate?.id,
    );

    if (!assignment) return;

    setValue(
      "vehicleAssignments",
      getUpdatedVehicleAssignments(
        vehicleAssignments,
        assignment,
        undefined,
        insurance,
      ),
    );

    trackEvent({
      eventCategory: "My Details",
      eventAction: "Button Click",
      eventLabel: "Update Insurance Save",
    });
  };

  return (
    <Fragment>
      {vehicleAssignmentToUpdate &&
        vehicleAssignmentToUpdate?.vehicle?.mileage && (
          <UpdateMileageModal
            registration={vehicleAssignmentToUpdate.vehicle.registration}
            mileage={getLatestVehicleMileage(
              vehicleAssignmentToUpdate.vehicle.mileage,
            )}
            onDiscardClick={handleUpdateMileageDiscardClick}
            onSaveClick={handleUpdateMileageSaveClick}
          />
        )}
      {updateInsuranceModal && (
        <InsuranceModal
          instructorId={instructorId}
          vehicle={vehicleToUpdate}
          open={updateInsuranceModal}
          onCancel={() => toggleUpdateInsuranceModal(false)}
          onComplete={handleUpdateInsuranceSaveClick}
        />
      )}
      {vehicleAssignments && (
        <DrivingDetails
          loading={loading}
          certifications={certifications}
          vehicleAssignments={vehicleAssignments}
          additionalDrivers={additionalDrivers}
          onMileageUpdateClick={handleUpdateMileageClick}
          onUpdateInsuranceClick={handleUpdateClick}
        />
      )}
    </Fragment>
  );
};

export default StateProvider;
